import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { hasPremiumAccess } from '../../utils/subscription';
import AdMock from './AdMock';
import { APP_PREFIX } from '../../config/appIdentifiers';

const SITE_NAME = process.env.REACT_APP_SITE_NAME;
const BETA_FREE_MODE = process.env.REACT_APP_BETA_FREE_MODE === 'true';

const AdSlot = styled.div`
  width: 100%;
  background: ${({ $placement }) => ($placement === 'header' ? '#f8fafc' : 'transparent')};
  border-bottom: ${({ $placement }) => ($placement === 'header' ? '1px solid #e5e7eb' : 'none')};
`;

const AdSlotInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  padding: ${({ $placement }) => ($placement === 'header' ? '0.5rem 1.5rem' : '1.25rem 1rem 1.75rem')};

  @media (max-width: 1024px) {
    padding: ${({ $placement }) => ($placement === 'header' ? '0.5rem 1rem' : '1rem 1rem 1.5rem')};
  }
`;

const GoogleAdWrapper = styled.div`
  width: 100%;
  max-width: ${({ $placement }) => ($placement === 'header' ? '980px' : '760px')};
  min-height: ${({ $placement }) => ($placement === 'header' ? '90px' : '120px')};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    min-height: 90px;
  }
`;

const ADSENSE_SCRIPT_ID = `${APP_PREFIX}-adsense-script`;
let adsenseScriptPromise = null;

const loadAdsenseScript = (clientId) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }

  if (!clientId) {
    return Promise.resolve(false);
  }

  if (adsenseScriptPromise) {
    return adsenseScriptPromise;
  }

  adsenseScriptPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(ADSENSE_SCRIPT_ID);
    if (existingScript) {
      if (Array.isArray(window.adsbygoogle)) {
        resolve(true);
        return;
      }

      // Retry cleanly if a previous script load failed (e.g. ad blocker toggled).
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = ADSENSE_SCRIPT_ID;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
    script.onload = () => resolve(true);
    script.onerror = () => {
      adsenseScriptPromise = null;
      script.remove();
      reject(new Error('Impossible de charger Google AdSense'));
    };
    document.head.appendChild(script);
  });

  return adsenseScriptPromise;
};

const GoogleAdUnit = ({ placement, clientId, slotId, adTestMode = false }) => {
  const adRef = useRef(null);
  const [scriptReady, setScriptReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;
    loadAdsenseScript(clientId)
      .then(() => {
        if (isMounted) {
          setScriptReady(true);
        }
      })
      .catch((error) => {
        console.error('AdSense script error:', error);
        if (isMounted) {
          setFailed(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (!scriptReady || failed || !adRef.current || typeof window === 'undefined') {
      return;
    }

    const node = adRef.current;
    const status = node.getAttribute('data-adsbygoogle-status');
    if (status === 'done') {
      return;
    }

    try {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      if (!/already have ads in them/i.test(message)) {
        console.error('AdSense push error:', error);
        setFailed(true);
      }
    }
  }, [scriptReady, failed]);

  if (failed) {
    return null;
  }

  return (
    <GoogleAdWrapper $placement={placement}>
      <ins
        ref={adRef}
        className="adsbygoogle"
        style={{ display: 'block', width: '100%' }}
        data-ad-client={clientId}
        data-ad-slot={slotId}
        data-ad-format="auto"
        data-full-width-responsive="true"
        data-adtest={adTestMode ? 'on' : undefined}
      />
    </GoogleAdWrapper>
  );
};

const adConfigs = {
  header: {
    guest: {
      headline: 'Creez votre journal en 2 minutes',
      description: 'Suivi calories & macros, objectifs clairs et interface rapide.',
      advertiser: SITE_NAME,
      ctaText: 'Commencer',
      imageUrl: '/logo192.png'
    },
    member: {
      headline: 'Passez au premium pour tout debloquer',
      description: 'Rapports detailles, macros avances et personnalisation complete.',
      advertiser: `${SITE_NAME} Premium`,
      ctaText: 'Voir les offres',
      imageUrl: '/logo192.png'
    }
  },
  footer: {
    guest: {
      headline: 'Healthy granola — 20% off',
      description: 'Crispy, low-sugar granola made from whole oats. Free delivery over €30.',
      advertiser: 'GranolaCo',
      ctaText: 'Shop now',
      imageUrl: null
    },
    member: {
      headline: 'Boostez votre energie naturellement',
      description: 'Snacks proteines et fibres pour tenir toute la journee.',
      advertiser: 'VitaSnack',
      ctaText: 'Decouvrir',
      imageUrl: null
    }
  }
};

const AppAdSlot = ({ placement = 'footer' }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const pathname = location?.pathname || '';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
  const premiumActive = hasPremiumAccess(user);
  const isDev = process.env.NODE_ENV !== 'production';
  const googleAdsEnabled = process.env.REACT_APP_GOOGLE_ADS_ENABLED === 'true';
  const allowGoogleAdsInDev = process.env.REACT_APP_GOOGLE_ADS_ENABLE_IN_DEV === 'true';
  const forceGoogleAdsOnLocalhost = process.env.REACT_APP_GOOGLE_ADS_FORCE_LOCALHOST === 'true';
  const forceShowAdsInDev = process.env.REACT_APP_GOOGLE_ADS_FORCE_SHOW_IN_DEV === 'true';
  const adTestMode = process.env.REACT_APP_GOOGLE_ADS_ADTEST === 'true';
  const clientId = process.env.REACT_APP_GOOGLE_ADS_CLIENT_ID || '';
  const headerSlotId = process.env.REACT_APP_GOOGLE_ADS_HEADER_SLOT || '';
  const footerSlotId = process.env.REACT_APP_GOOGLE_ADS_FOOTER_SLOT || '';
  const slotId = placement === 'header' ? headerSlotId : footerSlotId;
  const shouldUseGoogleAds =
    googleAdsEnabled
    && Boolean(clientId)
    && Boolean(slotId)
    && (!isDev || allowGoogleAdsInDev)
    && (!isLocalHost || forceGoogleAdsOnLocalhost);
  const hideOnPage =
    pathname === '/' ||
    pathname.startsWith('/about') ||
    pathname.startsWith('/login') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/admin');
  const bypassVisibilityGuards = isDev && forceShowAdsInDev;

  if (BETA_FREE_MODE) {
    return null;
  }

  if (!bypassVisibilityGuards && (hideOnPage || loading || premiumActive)) {
    return null;
  }

  const audience = premiumActive ? 'member' : 'guest';
  const fallback = adConfigs.footer.member;
  const adConfig = adConfigs[placement]?.[audience] || fallback;

  return (
    <AdSlot $placement={placement}>
      <AdSlotInner $placement={placement}>
        {shouldUseGoogleAds ? (
          <GoogleAdUnit
            placement={placement}
            clientId={clientId}
            slotId={slotId}
            adTestMode={adTestMode}
          />
        ) : (
          <AdMock {...adConfig} />
        )}
      </AdSlotInner>
    </AdSlot>
  );
};

export default AppAdSlot;
